import React, { ReactNode } from "react";
import {
  DashboardOutlined,
  ProjectOutlined,
  ClockCircleOutlined,
  OrderedListOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  WalletOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import type { NavigateFunction } from "react-router-dom";

// ---------------------------------------------------------------------------
// Single source of truth for the project-level navigation. Consumed by
// ProjectSidebar (desktop) and MobileNavigation (< 992px). The antd Menu items,
// the selected/open key resolution and the accordion root keys all derive from
// this one array. `pathPatterns` are matched with
// location.pathname.includes(pattern); the most-specific (longest) pattern
// wins, so e.g. "/expenses/reports" beats the analytics "/reports".
//
// Only the nine top-level entries carry icons: child rows are distinguished by
// indentation and the submenu rail (see project-sidebar.css), which keeps the
// label column wide enough for names like "Weighted Avg. Rate Summary" at the
// A+/A++ font scales.
// ---------------------------------------------------------------------------
export interface SideNavChild {
  key: string;
  label: string;
  /** Optional — children render without icons today. */
  icon?: ReactNode;
  /** Navigates to /pms/project/{route}/{projectId} */
  route?: string;
  /** Navigates to /pms/expenses/{expenseRoute}/{projectId} */
  expenseRoute?: string;
  /** Navigates to {absolutePath}/{projectId || ""} */
  absolutePath?: string;
  pathPatterns: string[];
}

export interface SideNavGroup {
  key: string;
  label: string;
  icon: ReactNode;
  children?: SideNavChild[];
  route?: string;
  absolutePath?: string;
  pathPatterns?: string[];
}

export const SIDE_NAV: SideNavGroup[] = [
  {
    key: "dashboard-overview",
    label: "Dashboard & Overview",
    icon: <DashboardOutlined />,
    children: [
      { key: "view", label: "Dashboard", route: "view", pathPatterns: ["/view"] },
      {
        key: "dashboard",
        label: "Task Insights",
        route: "dashboard",
        pathPatterns: ["/dashboard"],
      },
    ],
  },
  {
    key: "planning-execution",
    label: "Planning & Execution",
    icon: <ProjectOutlined />,
    children: [
      { key: "wbs", label: "WBS", route: "wbs", pathPatterns: ["/wbs"] },
      { key: "phases", label: "Phases", route: "phases", pathPatterns: ["/phases"] },
      {
        key: "activities",
        label: "Activities",
        route: "activities",
        pathPatterns: ["/activities"],
      },
      {
        key: "tasks",
        label: "Tasks",
        route: "tasks",
        pathPatterns: ["/tasks", "/task-detail"],
      },
      {
        key: "daily-execution",
        label: "Daily Execution",
        route: "daily-execution",
        pathPatterns: ["/daily-execution"],
      },
      {
        key: "task-monitoring",
        label: "Task Monitoring",
        route: "task-monitoring",
        pathPatterns: ["/task-monitoring"],
      },
      {
        key: "drives",
        label: "Drives",
        route: "drives",
        pathPatterns: ["/drives"],
      },
      { key: "issues", label: "Issues", route: "issues", pathPatterns: ["/issues"] },
      {
        key: "daily-approvals",
        label: "Daily Approvals",
        route: "daily-approvals",
        pathPatterns: ["/daily-approvals"],
      },
    ],
  },
  {
    key: "inventoryanalysis",
    label: "Material Forecast",
    icon: <DatabaseOutlined />,
    route: "inventory-analysis",
    pathPatterns: ["/inventory-analysis"],
  },
  {
    key: "expenses",
    label: "Expenses",
    icon: <WalletOutlined />,
    children: [
      {
        key: "expense-header",
        label: "Expense Header",
        expenseRoute: "reports",
        pathPatterns: ["/expenses/reports", "/expenses/new-header"],
      },
      {
        key: "new-expense",
        label: "New Expense",
        expenseRoute: "new-expense",
        pathPatterns: ["/expenses/new-expense"],
      },
      {
        key: "payment-in",
        label: "Payment In",
        expenseRoute: "payment-advances",
        pathPatterns: ["/expenses/payment-advances", "/expenses/new-advance"],
      },
      {
        key: "pending-approval",
        label: "Pending Approval",
        expenseRoute: "pending-approval",
        pathPatterns: ["/expenses/pending-approval"],
      },
      {
        key: "expense-ledger",
        label: "Ledger",
        expenseRoute: "ledger",
        pathPatterns: ["/expenses/ledger"],
      },
      {
        key: "expense-reports",
        label: "Expense Reports",
        expenseRoute: "ExpensesReports",
        pathPatterns: ["/expenses/ExpensesReports", "/expenses/expense-"],
      },
    ],
  },
  {
    key: "time-resource",
    label: "Time & Resources",
    icon: <ClockCircleOutlined />,
    children: [
      {
        key: "timesheet",
        label: "Timesheet",
        route: "timesheet",
        pathPatterns: ["/timesheet"],
      },
      {
        key: "timelogsettings",
        label: "Time Log Settings",
        route: "time-log-settings",
        pathPatterns: ["/time-log-settings"],
      },
    ],
  },
  {
    key: "collaboration",
    label: "Collaboration & Docs",
    icon: <FileTextOutlined />,
    children: [
      {
        key: "documents",
        label: "Documents",
        route: "documents",
        pathPatterns: ["/documents"],
      },
      {
        key: "projectusers",
        label: "Project Users",
        route: "project-users",
        pathPatterns: ["/project-users"],
      },
    ],
  },
  {
    key: "analytics",
    label: "Analytics & Reports",
    icon: <BarChartOutlined />,
    children: [
      {
        key: "reports",
        label: "Reports",
        route: "reports",
        // "/expenses/reports" is longer, so expense pages never match this.
        pathPatterns: ["/reports"],
      },
      {
        key: "budgetanalysis",
        label: "Budget Analysis",
        route: "budget-analysis",
        pathPatterns: ["/budget-analysis"],
      },
      {
        key: "threemconsolidation",
        label: "3M Consolidation",
        route: "3m-consolidation",
        pathPatterns: ["/3m-consolidation"],
      },
      {
        key: "materialcostanalysis",
        label: "Material Cost Analysis",
        route: "material-cost-analysis",
        pathPatterns: ["/material-cost-analysis"],
      },
      {
        key: "manpowercostanalysis",
        label: "Manpower Cost Analysis",
        route: "manpower-cost-analysis",
        pathPatterns: ["/manpower-cost-analysis"],
      },
      {
        key: "machinerycostanalysis",
        label: "Machinery Cost Analysis",
        route: "machinery-cost-analysis",
        pathPatterns: ["/machinery-cost-analysis"],
      },
      {
        key: "threemdeviationsummary",
        label: "3M Deviation Summary",
        route: "deviation-summary",
        pathPatterns: ["/deviation-summary"],
      },
      {
        key: "internaltasksummary",
        label: "Weighted Avg. Rate Summary",
        route: "internal-task-summary",
        pathPatterns: ["/internal-task-summary"],
      },
      {
        key: "monthlypnl",
        label: "Top Sheet",
        route: "monthly-pnl",
        pathPatterns: ["/monthly-pnl", "/monthly-pnl-month"],
      },
      {
        key: "projectcashentries",
        label: "Project Cash Entries",
        route: "project-cash-entries",
        pathPatterns: ["/project-cash-entries"],
      },
      {
        key: "monthlyexecution",
        label: "Monthly Execution",
        route: "monthly-execution",
        pathPatterns: ["/monthly-execution", "/monthly-execution-month"],
      },
    ],
  },
  {
    key: "imports-exports",
    label: "Imports & Exports",
    icon: <SwapOutlined />,
    children: [
      {
        key: "imports",
        label: "Imports",
        absolutePath: "/pms/bulk-upload-list",
        pathPatterns: ["/bulk-upload-list"],
      },
      {
        key: "exports",
        label: "Exports",
        absolutePath: "/pms/excelexport-requested-list",
        pathPatterns: ["/excelexport-requested-list"],
      },
    ],
  },
  {
    key: "sequence",
    label: "Sequence",
    icon: <OrderedListOutlined />,
    absolutePath: "/pms/sequence/SequenceList",
    pathPatterns: ["/sequence"],
  },
];

// Root submenu keys for accordion behavior (only one open at a time)
export const ROOT_SUBMENU_KEYS = SIDE_NAV.filter(
  (group) => group.children?.length,
).map((group) => group.key);

// Resolve the most-specific matching nav entry for the current path.
export const findNavMatch = (
  path: string,
): { selectedKey: string; openKey: string | null } | null => {
  let best: {
    selectedKey: string;
    openKey: string | null;
    length: number;
  } | null = null;

  const consider = (
    pattern: string,
    selectedKey: string,
    openKey: string | null,
  ) => {
    if (path.includes(pattern) && (!best || pattern.length > best.length)) {
      best = { selectedKey, openKey, length: pattern.length };
    }
  };

  for (const group of SIDE_NAV) {
    for (const pattern of group.pathPatterns || []) {
      consider(pattern, group.key, null);
    }
    for (const child of group.children || []) {
      for (const pattern of child.pathPatterns) {
        consider(pattern, child.key, group.key);
      }
    }
  }

  return best;
};

// Case-insensitive label filter for the sidebar's find box. A group whose own
// label matches keeps all of its children; otherwise only matching children
// survive. Groups with no surviving children are dropped.
export const filterSideNav = (query: string): SideNavGroup[] => {
  const q = query.trim().toLowerCase();
  if (!q) return SIDE_NAV;

  const out: SideNavGroup[] = [];
  for (const group of SIDE_NAV) {
    const groupHit = group.label.toLowerCase().includes(q);
    if (!group.children?.length) {
      if (groupHit) out.push(group);
      continue;
    }
    if (groupHit) {
      out.push(group);
      continue;
    }
    const children = group.children.filter((child) =>
      child.label.toLowerCase().includes(q),
    );
    if (children.length) out.push({ ...group, children });
  }
  return out;
};

/**
 * The three navigation flavours a nav entry can use, bound to a router
 * navigate + the resolved project. Shared by the desktop sidebar and the
 * mobile drawer so the URL shapes can never drift apart.
 */
export const makeNavHandlers = (
  navigate: NavigateFunction,
  projectId: string | null,
  locationState: unknown,
  projectName: string | null,
) => {
  const base = (locationState as Record<string, unknown>) || {};

  const goProject = (path: string) => {
    const state = { ...base, id: projectId, projectId, projectName };
    navigate(
      projectId ? `/pms/project/${path}/${projectId}` : `/pms/project/${path}`,
      { state },
    );
  };

  // Omit the projectId segment when it isn't resolved (avoids URLs like
  // /pms/expenses/reports/null).
  const goExpense = (subpath: string) => {
    const state = { ...base, id: projectId, projectId, projectName };
    navigate(
      projectId
        ? `/pms/expenses/${subpath}/${projectId}`
        : `/pms/expenses/${subpath}`,
      { state },
    );
  };

  const goAbsolute = (basePath: string) => {
    navigate(`${basePath}/${projectId || ""}`, {
      state: { ...base, id: projectId, projectName },
    });
  };

  const navigateItem = (item: {
    route?: string;
    expenseRoute?: string;
    absolutePath?: string;
  }) => {
    if (item.route) goProject(item.route);
    else if (item.expenseRoute) goExpense(item.expenseRoute);
    else if (item.absolutePath) goAbsolute(item.absolutePath);
  };

  return { goProject, goExpense, goAbsolute, navigateItem };
};
